import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useUI } from "../../Context/UIContext";
import CatalogToolbarSelect from "./CatalogToolbarSelect";
import ImportReportRecipientsEditor from "./ImportReportRecipientsEditor";
import ScheduledTimesEditor from "./ScheduledTimesEditor";
import LegendDialog, { CatalogLegendLink } from "./LegendDialog";
import {
  getCatalogServiceConfig,
  updateCatalogServiceConfig,
} from "./services/catalogMappingService";
import {
  LOG_LEVEL_OPTIONS,
  SCHEDULE_MODE_OPTIONS,
  configToDraft,
  draftToPayload,
} from "./utils/serviceConfigUtils";
import {
  SERVICE_CONFIG_LEGEND_SECTIONS,
  SERVICE_CONFIG_LEGEND_TITLE,
} from "./utils/serviceConfigLegendsContent";

const ConfigField = ({ label, children, fullWidth = false }) => (
  <div className={`cm-config-field${fullWidth ? " cm-config-field-full" : ""}`}>
    <label className="cm-field-label">{label}</label>
    <div className="cm-config-control">{children}</div>
  </div>
);

ConfigField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  fullWidth: PropTypes.bool,
};

const ConfigSection = ({ title, children }) => (
  <section className="cm-config-section">
    <h3 className="cm-config-section-title">{title}</h3>
    <div className="cm-config-stack">{children}</div>
  </section>
);

ConfigSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const CatalogServiceConfigDialog = ({ open, onClose }) => {
  const { showError, showSuccess } = useUI();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const [draft, setDraft] = useState(() => configToDraft({}));

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCatalogServiceConfig();
      setDraft(configToDraft(data));
    } catch (error) {
      showError(error.message || "Errore nel caricamento configurazione servizio");
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (open) {
      loadConfig();
    }
  }, [open, loadConfig]);

  const updateDraft = (patch) => {
    setDraft((previous) => ({ ...previous, ...patch }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await updateCatalogServiceConfig(draftToPayload(draft));
      setDraft(configToDraft(saved));
      showSuccess("Configurazione servizio aggiornata.");
      onClose();
    } catch (error) {
      showError(error.message || "Errore nel salvataggio configurazione servizio");
    } finally {
      setSaving(false);
    }
  };

  const disabled = loading || saving;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        PaperProps={{
          className: "cm-dialog cm-service-config-dialog catalog-maintenance",
          "data-theme": "light",
        }}
      >
        <DialogTitle className="cm-dialog-title">
          <Stack direction="row" spacing={1} alignItems="center" className="cm-dialog-title-row">
            <SettingsOutlinedIcon fontSize="small" />
            <span>Configurazione servizio import catalogo</span>
            <CatalogLegendLink
              onClick={() => setLegendOpen(true)}
              title={SERVICE_CONFIG_LEGEND_TITLE}
            />
          </Stack>
          <IconButton
            aria-label="Chiudi configurazione servizio"
            onClick={onClose}
            className="cm-dialog-close"
            size="small"
            disabled={saving}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent className="cm-dialog-body cm-service-config-body" dividers>
          <ConfigSection title="Import">
            <div className="cm-config-field cm-config-field-full">
              <FormControlLabel
                className="cm-config-switch"
                control={(
                  <Switch
                    checked={Boolean(draft.importEnabled)}
                    onChange={(event) => updateDraft({ importEnabled: event.target.checked })}
                    disabled={disabled}
                  />
                )}
                label="Import catalogo abilitati"
              />
            </div>
          </ConfigSection>

          <ConfigSection title="Scheduling">
            <ConfigField label="Modalita">
              <CatalogToolbarSelect
                value={draft.scheduleMode}
                onChange={(value) => updateDraft({ scheduleMode: value })}
                options={SCHEDULE_MODE_OPTIONS}
                disabled={disabled}
                ariaLabel="Modalita scheduling import"
              />
            </ConfigField>

            {draft.scheduleMode === "times" && (
              <ConfigField label="Orari (Europe/Rome)">
                <ScheduledTimesEditor
                  value={draft.scheduledTimesRomeList}
                  onChange={(scheduledTimesRomeList) => updateDraft({ scheduledTimesRomeList })}
                  disabled={disabled}
                />
              </ConfigField>
            )}
          </ConfigSection>

          <ConfigSection title="Prestazioni import">
            <ConfigField label="Batch vendor">
              <input
                className="cm-input"
                type="number"
                min={1}
                value={draft.importVendorBatchSize}
                onChange={(event) => updateDraft({ importVendorBatchSize: event.target.value })}
                disabled={disabled}
              />
            </ConfigField>
            <ConfigField label="Pausa batch vendor (sec)">
              <input
                className="cm-input"
                type="number"
                min={0}
                value={draft.importVendorPauseSeconds}
                onChange={(event) => updateDraft({ importVendorPauseSeconds: event.target.value })}
                disabled={disabled}
              />
            </ConfigField>
            <ConfigField label="Batch upsert righe">
              <input
                className="cm-input"
                type="number"
                min={1}
                value={draft.importBatchSize}
                onChange={(event) => updateDraft({ importBatchSize: event.target.value })}
                disabled={disabled}
              />
            </ConfigField>
            <ConfigField label="Chunk SQL bulk">
              <input
                className="cm-input"
                type="number"
                min={1}
                value={draft.importBulkExecuteChunkSize}
                onChange={(event) => updateDraft({ importBulkExecuteChunkSize: event.target.value })}
                disabled={disabled}
              />
            </ConfigField>
            <ConfigField label="Soglia temp table ID visti">
              <input
                className="cm-input"
                type="number"
                min={1}
                value={draft.importSeenTempTableThreshold}
                onChange={(event) => updateDraft({ importSeenTempTableThreshold: event.target.value })}
                disabled={disabled}
              />
            </ConfigField>
          </ConfigSection>

          <ConfigSection title="Azioni HTTP post-import">
            <div className="cm-config-field cm-config-field-full">
              <FormControlLabel
                className="cm-config-switch"
                control={(
                  <Switch
                    checked={Boolean(draft.reindexFullAfterScheduledImport)}
                    onChange={(event) => updateDraft({ reindexFullAfterScheduledImport: event.target.checked })}
                    disabled={disabled}
                  />
                )}
                label="Reindex dopo import schedulato"
              />
            </div>

            <div className="cm-config-field cm-config-field-full">
              <FormControlLabel
                className="cm-config-switch"
                control={(
                  <Switch
                    checked={Boolean(draft.reindexFullAfterManualAllImport)}
                    onChange={(event) => updateDraft({ reindexFullAfterManualAllImport: event.target.checked })}
                    disabled={disabled}
                  />
                )}
                label="Reindex dopo import manuale totale (Importa tutti)"
              />
            </div>

            <div className="cm-config-field cm-config-field-full">
              <FormControlLabel
                className="cm-config-switch"
                control={(
                  <Switch
                    checked={Boolean(draft.reindexVendorAfterManualSelectedImport)}
                    onChange={(event) => updateDraft({ reindexVendorAfterManualSelectedImport: event.target.checked })}
                    disabled={disabled}
                  />
                )}
                label="Reindex vendor dopo import manuale selezionati (full)"
              />
            </div>

            <div className="cm-config-field cm-config-field-full">
              <FormControlLabel
                className="cm-config-switch"
                control={(
                  <Switch
                    checked={Boolean(draft.reindexVendorAfterManualSingleImport)}
                    onChange={(event) => updateDraft({ reindexVendorAfterManualSingleImport: event.target.checked })}
                    disabled={disabled}
                  />
                )}
                label="Reindex vendor dopo import manuale singolo full"
              />
            </div>

            <div className="cm-config-field cm-config-field-full">
              <FormControlLabel
                className="cm-config-switch"
                control={(
                  <Switch
                    checked={Boolean(draft.reindexVendorAfterManualStoredImport)}
                    onChange={(event) => updateDraft({ reindexVendorAfterManualStoredImport: event.target.checked })}
                    disabled={disabled}
                  />
                )}
                label="Reindex vendor dopo import manuale singolo (feed normalizzato)"
              />
            </div>

            <ConfigField label="Base URL RicercaProdotti (interno)">
              <input
                className="cm-input"
                type="text"
                value={draft.internalRicercaBaseUrl}
                onChange={(event) => updateDraft({ internalRicercaBaseUrl: event.target.value })}
                disabled={disabled}
              />
            </ConfigField>

            <ConfigField label="Timeout HTTP (sec)">
              <input
                className="cm-input"
                type="number"
                min={1}
                value={draft.httpTriggerTimeoutSeconds}
                onChange={(event) => updateDraft({ httpTriggerTimeoutSeconds: event.target.value })}
                disabled={disabled}
              />
            </ConfigField>

            <div className="cm-config-field cm-config-field-full">
              <FormControlLabel
                className="cm-config-switch"
                control={(
                  <Switch
                    checked={Boolean(draft.persistImportJobs)}
                    onChange={(event) => updateDraft({ persistImportJobs: event.target.checked })}
                    disabled={disabled}
                  />
                )}
                label="Registra job import in DB (import_jobs)"
              />
            </div>
          </ConfigSection>

          <ConfigSection title="Notifiche email">
            <div className="cm-config-field cm-config-field-full">
              <FormControlLabel
                className="cm-config-switch"
                control={(
                  <Switch
                    checked={Boolean(draft.sendEmailAfterScheduledImport)}
                    onChange={(event) => updateDraft({ sendEmailAfterScheduledImport: event.target.checked })}
                    disabled={disabled}
                  />
                )}
                label="Email report Excel dopo import schedulato"
              />
            </div>

            <div className="cm-config-field cm-config-field-full">
              <FormControlLabel
                className="cm-config-switch"
                control={(
                  <Switch
                    checked={Boolean(draft.sendEmailAfterManualAllImport)}
                    onChange={(event) => updateDraft({ sendEmailAfterManualAllImport: event.target.checked })}
                    disabled={disabled}
                  />
                )}
                label="Email report Excel dopo import manuale totale (Importa tutti)"
              />
            </div>

            <div className="cm-config-field cm-config-field-full">
              <FormControlLabel
                className="cm-config-switch"
                control={(
                  <Switch
                    checked={Boolean(draft.sendEmailAfterManualSelectedImport)}
                    onChange={(event) => updateDraft({ sendEmailAfterManualSelectedImport: event.target.checked })}
                    disabled={disabled}
                  />
                )}
                label="Email report Excel dopo import manuale selezionati (full)"
              />
            </div>

            <div className="cm-config-field cm-config-field-full">
              <FormControlLabel
                className="cm-config-switch"
                control={(
                  <Switch
                    checked={Boolean(draft.sendEmailAfterManualSingleImport)}
                    onChange={(event) => updateDraft({ sendEmailAfterManualSingleImport: event.target.checked })}
                    disabled={disabled}
                  />
                )}
                label="Email report Excel dopo import manuale singolo full"
              />
            </div>

            <div className="cm-config-field cm-config-field-full">
              <FormControlLabel
                className="cm-config-switch"
                control={(
                  <Switch
                    checked={Boolean(draft.sendEmailAfterManualStoredImport)}
                    onChange={(event) => updateDraft({ sendEmailAfterManualStoredImport: event.target.checked })}
                    disabled={disabled}
                  />
                )}
                label="Email report Excel dopo import manuale singolo (feed normalizzato)"
              />
            </div>

            <ConfigField label="Destinatari report" fullWidth>
              <ImportReportRecipientsEditor
                value={draft.importReportRecipients}
                onChange={(importReportRecipients) => updateDraft({ importReportRecipients })}
                disabled={disabled}
              />
            </ConfigField>
          </ConfigSection>

          <ConfigSection title="Sistema">
            <ConfigField label="Directory temporanea">
              <input
                className="cm-input"
                type="text"
                value={draft.tmpDir}
                onChange={(event) => updateDraft({ tmpDir: event.target.value })}
                disabled={disabled}
              />
            </ConfigField>
            <ConfigField label="Log level">
              <CatalogToolbarSelect
                value={draft.logLevel}
                onChange={(value) => updateDraft({ logLevel: value })}
                options={LOG_LEVEL_OPTIONS}
                disabled={disabled}
                ariaLabel="Log level servizio"
              />
            </ConfigField>
          </ConfigSection>
        </DialogContent>

        <DialogActions className="cm-dialog-actions">
          <Button className="cm-toolbar-btn" onClick={onClose} disabled={saving}>Annulla</Button>
          <Button className="cm-toolbar-btn" variant="contained" onClick={handleSave} disabled={disabled}>
            {saving ? "Salvataggio..." : "Salva"}
          </Button>
        </DialogActions>
      </Dialog>

      <LegendDialog
        open={legendOpen}
        onClose={() => setLegendOpen(false)}
        sections={SERVICE_CONFIG_LEGEND_SECTIONS}
      />
    </>
  );
};

CatalogServiceConfigDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CatalogServiceConfigDialog;
